import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, Layers, Plus, Tag, X } from 'lucide-react';
import {
  type PriceType,
  type Variation,
  createPriceType,
  createVariation,
  listPriceTypes,
  listVariations,
  setVariationPrice,
} from '@/api/catalogVariations';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface Props {
  parentProductId: string;
  productName: string;
  onClose: () => void;
}

export function VariationsDrawer({ parentProductId, productName, onClose }: Props) {
  const { t } = useTranslation();
  const [variations, setVariations] = useState<Variation[]>([]);
  const [priceTypes, setPriceTypes] = useState<PriceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showPriceTypes, setShowPriceTypes] = useState(false);
  const [priceTarget, setPriceTarget] = useState<Variation | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([listVariations(parentProductId).catch(() => []), listPriceTypes().catch(() => [])])
      .then(([v, p]) => {
        setVariations(v);
        setPriceTypes(p);
      })
      .finally(() => setLoading(false));
  }, [parentProductId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-lg flex-col border-l bg-card shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="variations-drawer"
      >
        <header className="flex items-start justify-between border-b px-4 py-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-[var(--color-lop-text-muted)]">
              {t('catalogVariations.title')}
            </p>
            <h2 className="truncate text-lg font-semibold text-[var(--color-lop-text-primary)]">{productName}</h2>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/5" data-testid="variations-close">
            <X className="h-5 w-5 text-[var(--color-lop-text-muted)]" />
          </button>
        </header>

        <div className="flex items-center justify-between border-b px-4 py-2">
          <Button size="sm" variant="ghost" onClick={() => setShowPriceTypes((v) => !v)} data-testid="price-types-toggle">
            <Tag className="mr-1 h-4 w-4" />
            {t('catalogVariations.priceTypes')} ({priceTypes.length})
          </Button>
          <Button size="sm" onClick={() => setShowNew(true)} data-testid="variation-new">
            <Plus className="mr-1 h-4 w-4" />
            {t('catalogVariations.newVariation')}
          </Button>
        </div>

        {showPriceTypes && (
          <PriceTypesPanel priceTypes={priceTypes} onChanged={load} />
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {loading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!loading && variations.length === 0 && (
            <p className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground">
              <Layers className="h-8 w-8 opacity-40" />
              {t('catalogVariations.empty')}
            </p>
          )}
          <ul className="space-y-2">
            {variations.map((v) => (
              <li key={v.id} className="rounded-lg border px-3 py-2" data-testid="variation-row">
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-[var(--color-lop-text-primary)]">{v.name}</p>
                    <p className="truncate font-mono text-xs text-[var(--color-lop-text-muted)]">{v.sku}</p>
                  </div>
                  {v.price != null && (
                    <span className="text-sm text-[var(--color-lop-text-secondary)]">
                      {v.price} {v.currency ?? ''}
                    </span>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setPriceTarget(v)}
                    disabled={priceTypes.length === 0}
                    data-testid="variation-set-price"
                  >
                    <DollarSign className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {showNew && (
        <NewVariationModal
          parentProductId={parentProductId}
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            load();
          }}
        />
      )}
      {priceTarget && (
        <SetPriceModal
          variation={priceTarget}
          priceTypes={priceTypes}
          onClose={() => setPriceTarget(null)}
          onSaved={() => {
            setPriceTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function PriceTypesPanel({ priceTypes, onChanged }: { priceTypes: PriceType[]; onChanged: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [busy, setBusy] = useState(false);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    setBusy(true);
    try {
      await createPriceType({ code: code.trim(), name: name.trim(), baseCurrency: currency.trim().toUpperCase() });
      setCode('');
      setName('');
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border-b bg-muted/20 px-4 py-3">
      <div className="mb-2 flex flex-wrap gap-1">
        {priceTypes.map((p) => (
          <span key={p.id} className="rounded-full bg-zinc-800 px-2 py-0.5 text-[10px] text-zinc-300">
            {p.name} · {p.baseCurrency}
            {p.isDefault ? ' ★' : ''}
          </span>
        ))}
        {priceTypes.length === 0 && (
          <span className="text-xs text-muted-foreground">{t('catalogVariations.noPriceTypes')}</span>
        )}
      </div>
      <form onSubmit={add} className="flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder={t('catalogVariations.ptCode')} className="w-24" data-testid="price-type-code" />
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('catalogVariations.ptName')} data-testid="price-type-name" />
        <Input value={currency} onChange={(e) => setCurrency(e.target.value)} className="w-20" maxLength={3} />
        <Button type="submit" size="sm" disabled={busy} data-testid="price-type-add">
          {t('catalogVariations.add')}
        </Button>
      </form>
    </div>
  );
}

function NewVariationModal({
  parentProductId,
  onClose,
  onCreated,
}: {
  parentProductId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!sku.trim() || !name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const priceNum = price.trim() ? Number(price) : undefined;
      await createVariation({
        parentProductId,
        sku: sku.trim(),
        name: name.trim(),
        price: priceNum != null && Number.isFinite(priceNum) ? priceNum : undefined,
        currency: price.trim() ? currency.trim().toUpperCase() : undefined,
      });
      onCreated();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to create variation');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('catalogVariations.newModal.title')}</h3>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('catalogVariations.newModal.sku')}
        </label>
        <Input value={sku} onChange={(e) => setSku(e.target.value)} className="mb-3 font-mono" data-testid="variation-sku" />
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('catalogVariations.newModal.name')}
        </label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mb-3" data-testid="variation-name" />
        <div className="mb-4 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('catalogVariations.newModal.price')}
            </label>
            <Input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="variation-price" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('catalogVariations.newModal.currency')}
            </label>
            <Input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={3} />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting} data-testid="variation-submit">
            {t('common.create')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SetPriceModal({
  variation,
  priceTypes,
  onClose,
  onSaved,
}: {
  variation: Variation;
  priceTypes: PriceType[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [priceTypeId, setPriceTypeId] = useState(priceTypes[0]?.id ?? '');
  const [price, setPrice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = priceTypes.find((p) => p.id === priceTypeId);

  async function submit() {
    const n = Number(price);
    if (!priceTypeId || !Number.isFinite(n) || n < 0) {
      setError(t('catalogVariations.priceModal.invalid'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await setVariationPrice(variation.id, priceTypeId, n, selected?.baseCurrency ?? 'USD');
      onSaved();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to set price');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-1 text-lg font-semibold">{t('catalogVariations.priceModal.title')}</h3>
        <p className="mb-4 truncate text-xs text-[var(--color-lop-text-muted)]">{variation.name}</p>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('catalogVariations.priceModal.priceType')}
        </label>
        <select
          value={priceTypeId}
          onChange={(e) => setPriceTypeId(e.target.value)}
          className="mb-3 w-full rounded-md border border-[var(--color-lop-border)] bg-transparent px-3 py-2 text-sm"
          data-testid="set-price-type"
        >
          {priceTypes.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} ({p.baseCurrency})
            </option>
          ))}
        </select>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('catalogVariations.priceModal.price')} ({selected?.baseCurrency ?? 'USD'})
        </label>
        <Input
          type="number"
          min="0"
          step="0.01"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="mb-4"
          data-testid="set-price-amount"
        />
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting} data-testid="set-price-submit">
            {t('common.save')}
          </Button>
        </div>
      </div>
    </div>
  );
}
